import React, { useState, useEffect, useMemo } from 'react';
import { Player, TeamInLeague, GameState } from '../../../domain/entities';
import { AiService } from '../../../services/AiService';
import { FlavorTextService } from '../../../services/FlavorTextService';

interface AiPunditTakeProps {
    player: Player;
    team: TeamInLeague;
    isAiPunditEnabled: boolean;
    gameState: GameState;
    onCacheAiPunditTake: (playerId: number, take: string) => void;
}

export const AiPunditTake: React.FC<AiPunditTakeProps> = ({ player, team, isAiPunditEnabled, gameState, onCacheAiPunditTake }) => {
    const [report, setReport] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const offlineReport = useMemo(() => FlavorTextService.getPunditTake(player), [player]);

    useEffect(() => {
        if (!isAiPunditEnabled) {
            setReport(null);
            setIsLoading(false);
            return;
        }

        const isStale = !player.aiPunditTake || 
                        player.aiPunditTake.seasonGenerated < gameState.season ||
                        (player.aiPunditTake.seasonGenerated === gameState.season && Math.floor(gameState.currentWeek) - player.aiPunditTake.weekGenerated > 4);

        if (!isStale && player.aiPunditTake) {
            setReport(player.aiPunditTake.take);
            setIsLoading(false);
            setError(null);
            return;
        }

        const generateReport = async () => {
            setIsLoading(true);
            setError(null);
            setReport(null);

            try {
                const result = await AiService.generatePunditTake(player, team);
                setReport(result);
                onCacheAiPunditTake(player.id, result);
            } catch (e) {
                console.error("Error generating AI pundit report:", e);
                setError("The AI Pundit is currently unavailable.");
            } finally {
                setIsLoading(false);
            }
        };

        generateReport();
    }, [player, team, isAiPunditEnabled, gameState, onCacheAiPunditTake]);


    const content = () => {
        if (!isAiPunditEnabled) {
            return <p style={{ fontStyle: 'italic', opacity: 0.9, margin: 0 }}>"{offlineReport}"</p>;
        }
    
        if (isLoading) {
            return <p style={{ fontStyle: 'italic', opacity: 0.9, margin: 0 }}>"Pundit is thinking..."</p>;
        }
    
        if (error) {
            return <p className="negative" style={{ fontStyle: 'italic', margin: 0 }}>"{error}"</p>;
        }
        
        if (report) {
            return <p style={{ fontStyle: 'italic', opacity: 0.9, margin: 0 }}>"{report}"</p>;
        }
    
        return <p style={{ fontStyle: 'italic', opacity: 0.9, margin: 0 }}>"{offlineReport}"</p>;
    }

    return (
        <div className="panel">
            <div className="panel-header"><i className="fas fa-newspaper" style={{ marginRight: '8px' }}></i>Pundit's Take</div>
            <div className="panel-content">
                {content()}
            </div>
        </div>
    );
};